import { Form, Input, Button, DatePicker, Upload, message, Select } from "antd";
import { BasicType, PhotoType, GradeType } from "./types";
import { useEffect, useRef, useState } from "react";
import type { UploadProps } from "antd/es/upload/interface";
import {
  CloseOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { localUploadBaseProps } from "../../../utils/localUploadRequest";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";
import requiredRule from "../../../utils/formRules/requiredRule";
import dayjs from "dayjs";
import { apiCall } from "../../../utils/apiCall";

interface BasicProps {
  basicData: BasicType | undefined;
  photoData: PhotoType | undefined;
  setBasicData: any;
  setPhotoData: any;
}

const BasicInformationTab = ({
  basicData,
  photoData,
  setBasicData,
  setPhotoData,
}: BasicProps) => {
  const [form] = Form.useForm();
  const modalRef = useRef<HTMLDivElement>(null);
  const [showAddressWindow, setShowAddressWindow] = useState(false);
  const [gradeOptions, setGradeOptions] = useState<GradeType[]>([]);
  const [selectedGrade, setSelectedGrade] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [fileName, setFileName] = useState("");

  const handleUpload = (file: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setPhotoUrl(reader.result as string);
      setFileName(file.name);
    };
  };

  const header = {
    headers: {
      "x-api-key": process.env.REACT_APP_X_API_KEY,
    },
  };

  const fetchData = async () => {
    const data = await apiCall({
      method: "GET",
      header: header,
      endPoint: process.env.REACT_APP_SALES_ADMIN + "/advisors/admin/grades",
    });
    if (data) {
      setGradeOptions(data);
    }
  };

  const props: UploadProps = {
    ...localUploadBaseProps,
    beforeUpload: (file) => {
      const isImage = file.type.startsWith("image");
      if (!isImage) {
        message.error(renderAntDMessageConfig("Wrong file format!"));
        return Upload.LIST_IGNORE;
      }
      handleUpload(file);
      return false;
    },
    showUploadList: false,
    maxCount: 1,
    listType: "picture",
  };

  const onChange = () => {
    const {
      englishName,
      phoneNumber,
      email,
      zip,
      address1,
      address2,
      birthday,
    } = form.getFieldsValue();

    const newValue = {
      englishName: englishName,
      phoneNumber: phoneNumber,
      email: email,
      zipCode: zip,
      address1: address1,
      address2: address2,
      birthday: birthday
        ? dayjs(birthday).format("YYYY-MM-DD") + "T00:00:00.000Z"
        : "",
      companyNumber: basicData?.companyNumber,
      name: basicData?.name,
      companyTitle:
        gradeOptions.find((i) => i.gradeName === selectedGrade)?.companyTitle ||
        "",
      gradeName: selectedGrade,
    };
    setBasicData(newValue);
  };

  useEffect(() => {
    setPhotoData({
      fileName: fileName,
      base64File: photoUrl.split("base64,")[1],
    });
  }, [photoUrl, fileName]);

  useEffect(() => {
    if (basicData) {
      setSelectedGrade(basicData.gradeName);
      form.setFieldsValue({
        englishName: basicData.englishName,
        phoneNumber: basicData.phoneNumber,
        email: basicData.email,
        zip: basicData.zipCode,
        address1: basicData.address1,
        address2: basicData.address2,
        birthday: basicData.birthday ? dayjs(basicData.birthday) : null,
        companyTitle: basicData.companyTitle,
        gradeName: basicData.gradeName,
      });
    }
  }, [form, basicData]);

  useEffect(() => {
    if (photoData?.data && photoData.contentType) {
      setPhotoUrl(`data:${photoData?.contentType};base64,${photoData?.data}`);
      setFileName(photoData.fileName);
    }
  }, [photoData]);

  useEffect(() => {
    if (gradeOptions.length > 0 && selectedGrade) {
      const companyTitle =
        gradeOptions.find((i) => i.gradeName === selectedGrade)?.companyTitle ||
        "";
      form.setFieldsValue({ companyTitle });
      setBasicData({
        ...basicData,
        gradeName: selectedGrade,
        companyTitle: companyTitle,
      });
    }
  }, [selectedGrade, gradeOptions, form]);

  useEffect(() => {
    const loadDaumPostcode = () => {
      new window.daum.Postcode({
        oncomplete: function (data: any) {
          form.setFieldsValue({
            zip: data?.zonecode,
            address1: data?.address,
          });
          setShowAddressWindow(false);
          onChange();
        },
        width: "100%",
        height: "100%",
      }).embed(modalRef.current);
    };

    if (!window.daum || !window.daum.Postcode) {
      const script = document.createElement("script");
      script.src =
        "https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
      script.async = true;
      script.onload = loadDaumPostcode;
      document.body.appendChild(script);
    } else {
      loadDaumPostcode();
    }
  }, [showAddressWindow]);

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="tab-container">
      <Form
        form={form}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 16 }}
        className="relative"
      >
        <Form.Item label="사번">{basicData?.companyNumber}</Form.Item>
        <Form.Item label="이름">{basicData?.name}</Form.Item>
        <Form.Item name="englishName" label="영문명">
          <Input className="w-1/2" onChange={onChange} />
        </Form.Item>
        <Form.Item
          name="phoneNumber"
          label="휴대폰번호"
          rules={[{ required: true, message: "휴대폰번호 입력은 필수입니다." }]}
        >
          <Input
            placeholder="휴대폰번호를 입력해주세요."
            className="w-1/2"
            onChange={onChange}
          />
        </Form.Item>
        <Form.Item name="email" label="이메일">
          <Input
            placeholder="example@gmail.com"
            className="w-4/5"
            onChange={onChange}
          />
        </Form.Item>
        <Form.Item label="주소" required>
          <div className="flex justify-between h-32px w-4/5">
            <Form.Item className="w-4/5" name="zip" rules={[requiredRule]}>
              <Input disabled onChange={onChange} />
            </Form.Item>
            <Form.Item className="ms-2">
              <Button
                icon={<SearchOutlined />}
                onClick={() => {
                  setShowAddressWindow(!showAddressWindow);
                }}
              >
                주소찾기
              </Button>
            </Form.Item>
          </div>
          <Form.Item
            name="address1"
            className="mt-0 mb-2 w-4/5"
            rules={[requiredRule]}
          >
            <Input disabled onChange={onChange} />
          </Form.Item>
          <Form.Item name="address2" className="mt-0 w-4/5">
            <Input onChange={onChange} />
          </Form.Item>
        </Form.Item>
        <Form.Item label="등급" name="gradeName" rules={[requiredRule]}>
          <Select
            className="w-4/5"
            options={gradeOptions.map((i) => {
              return {
                label: i.gradeName,
                value: i.gradeName,
              };
            })}
            onChange={(v) => {
              setSelectedGrade(v);
            }}
          />
        </Form.Item>
        <Form.Item label="AD/MGR직함" name="companyTitle">
          <Input className="w-4/5" disabled />
        </Form.Item>
        <Form.Item label="생년월일" name="birthday">
          <DatePicker
            className="w-4/5"
            placeholder="생년월일을 선택해주세요."
            onChange={onChange}
          />
        </Form.Item>
        {/* <Form.Item name="education" label="학력정보">
          <Input placeholder="학력정보를 입력해주세요." className="w-4/5" />
        </Form.Item> */}
        <div className="photo-area absolute">
          <img
            src={
              photoUrl
                ? photoUrl
                : require("../../../assets/icons/default-avatar.png")
            }
            className="file-pic"
            alt="file-pic"
          />
          <Upload name="avatar" listType="picture-circle" {...props}>
            <Button icon={<UploadOutlined />}>사진첨부</Button>
          </Upload>
        </div>
      </Form>
      {showAddressWindow && (
        <div className="address-modal">
          <div className="flex justify-between items-center title">
            <p>주소검색</p>
            <div
              onClick={() => {
                setShowAddressWindow(false);
              }}
            >
              <CloseOutlined />
            </div>
          </div>
          <div ref={modalRef} className="address-modal-2" />
        </div>
      )}
    </div>
  );
};

export default BasicInformationTab;
